import { TaglineBadge } from '@/components/brand/TaglineBadge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { trackFormSubmitted } from '@/lib/analytics-tracker';
import { useForm } from '@inertiajs/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { useEffect, useId, type FormEventHandler } from 'react';

/**
 * Form context for attribution. The agent-application form is only rendered
 * from /agents/become-an-agent/apply (inline + modal), so source defaults are
 * stable. The fallback attribution to the visitor's entry-article happens
 * SERVER-SIDE in AgentApplicationController via visitor.first_touch_article_id.
 *
 * IMPORTANT (admin-only contract): applications are NEVER surfaced back to the
 * attributed agent/author. Attribution is for our internal analytics only.
 * Enforcement: AgentApplicationPolicy + admin-only notification routing.
 */
export interface AgentApplyContext {
    source?: string;
    page_url?: string;
}

/* ------------------------------------------------------------------ */
/*  AgentApplyForm                                                      *
 *  Self-contained agent application form. Owns its own Inertia useForm
 *  state so it can be mounted both in-page (at the bottom of           *
 *  /agents/become-an-agent/apply) and inside the modal popup           *
 *  (AgentApplyModal). Each instance has independent state.             *
 *                                                                      *
 *  No auto-close on success — the modal stays open until the user      *
 *  closes it via the X button. Reserves user agency over the dismiss   *
 *  moment.                                                             */
/* ------------------------------------------------------------------ */

const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * Zoho's Lead_Intro_Information field holds 2,000 characters and rejects the entire
 * lead on overrun, so a longer answer used to mean the applicant never reached the
 * CRM at all. Mirrors StoreAgentApplicationRequest::WHY_THRIVE_MAX — change both.
 */
const WHY_THRIVE_MAX = 2000;

const INPUT_CLS =
    'lux-body lux-body-sm border-[#F5F5F5]/30 bg-transparent text-[#F5F5F5] transition-colors duration-200 placeholder:text-[#F5F5F5]/55 focus-visible:border-[#F5F5F5]/55 focus-visible:ring-[#F5F5F5]/10';

export function AgentApplyForm({ context }: { context?: AgentApplyContext } = {}) {
    const shouldReduceMotion = useReducedMotion();
    const formId = useId();

    const { data, setData, post, processing, errors, reset, wasSuccessful } = useForm({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        is_travel_agent: '',
        host_agency_name: '',
        annual_revenue: '',
        current_occupation: '',
        why_thrive: '',
        sms_opt_in: false as boolean,
        newsletter_opt_in: false as boolean,
        _hp_field: '',
        // Attribution fields — captured server-side from visitor cookie + first_touch_article_id.
        // Frontend just sends source + page_url; the article/agent attribution is resolved server-side.
        context_source: context?.source ?? 'agent_apply_page',
        context_page_url: context?.page_url ?? '',
    });

    // Set page_url on mount (browser-only) — useForm initial state runs during SSR-friendly hydration.
    useEffect(() => {
        if (!data.context_page_url && typeof window !== 'undefined') {
            setData('context_page_url', window.location.href);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit: FormEventHandler = (e) => {
        e.preventDefault();
        post('/agent-application', {
            preserveScroll: true,
            onSuccess: () => {
                reset();
                // Emit form_submitted analytics event so analytics_conversions
                // can count this application. Fire-and-forget; context.source
                // identifies which agent_apply surface (page vs. modal).
                trackFormSubmitted('agent_apply', {
                    form_label: 'Apply to Join Our Team',
                    apply_source: context?.source ?? 'agent_apply_page',
                    is_travel_agent: data.is_travel_agent || null,
                });
            },
        });
    };


    return (
        <motion.div
            className="mx-auto w-full max-w-3xl"
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: EASE }}
        >
            <AnimatePresence mode="wait">
                {wasSuccessful ? (
                    <motion.div
                        key="success"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="flex flex-col items-center py-12 text-center"
                        aria-live="polite"
                        role="status"
                    >
                        <CheckCircle className="mb-6 h-12 w-12 text-[#F5F5F5]/80" strokeWidth={1} />
                        <h2 className="lux-didot lux-didot-md mb-3 text-[#F5F5F5]">Application Received</h2>
                        <p className="lux-body lux-body-sm text-[#F5F5F5]/60">
                            Thank you for your application — we read every one personally. You&rsquo;ll hear back from us within 48 hours.
                        </p>
                    </motion.div>
                ) : (
                    <motion.form
                        key="form"
                        onSubmit={submit}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-4"
                        role="form"
                        aria-labelledby={`${formId}-heading`}
                    >
                        <div className="mb-8 text-center">
                            <h2 id={`${formId}-heading`} className="lux-didot lux-didot-lg text-[#F5F5F5]">
                                Apply to Join Our Team
                            </h2>
                            <TaglineBadge
                                text="COMPREHENSIVE TRAINING · SUPERIOR TECH · HIGHER EARNINGS"
                                className="mx-auto mt-5 h-[9px] w-auto max-w-full text-[#F5F5F5]/50"
                            />
                        </div>

                        {/* Honeypot */}
                        <div className="absolute -left-[9999px]" aria-hidden="true">
                            <input
                                type="text"
                                name="_hp_field"
                                tabIndex={-1}
                                autoComplete="off"
                                value={data._hp_field}
                                onChange={(e) => setData('_hp_field', e.target.value)}
                            />
                        </div>

                        {/* Name row */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label htmlFor={`${formId}-first_name`} className="lux-caps lux-caps-sm mb-2 block text-[#F5F5F5]/70">
                                    First Name
                                </label>
                                <Input
                                    id={`${formId}-first_name`}
                                    value={data.first_name}
                                    onChange={(e) => setData('first_name', e.target.value)}
                                    className={INPUT_CLS}
                                    aria-describedby={errors.first_name ? `${formId}-first_name-error` : undefined}
                                    required
                                />
                                {errors.first_name && (
                                    <p id={`${formId}-first_name-error`} role="alert" className="lux-body lux-body-sm mt-1 text-[#F87171]">
                                        {errors.first_name}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label htmlFor={`${formId}-last_name`} className="lux-caps lux-caps-sm mb-2 block text-[#F5F5F5]/70">
                                    Last Name
                                </label>
                                <Input
                                    id={`${formId}-last_name`}
                                    value={data.last_name}
                                    onChange={(e) => setData('last_name', e.target.value)}
                                    className={INPUT_CLS}
                                    aria-describedby={errors.last_name ? `${formId}-last_name-error` : undefined}
                                    required
                                />
                                {errors.last_name && (
                                    <p id={`${formId}-last_name-error`} role="alert" className="lux-body lux-body-sm mt-1 text-[#F87171]">
                                        {errors.last_name}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Phone + Email row — phone gets a wider share so the country selector + number breathe */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1.1fr_1fr]">
                            <div>
                                <label htmlFor={`${formId}-phone`} className="lux-caps lux-caps-sm mb-2 block text-[#F5F5F5]/70">
                                    Phone
                                </label>
                                <PhoneInput
                                    id={`${formId}-phone`}
                                    value={data.phone}
                                    onChange={(val) => setData('phone', val ?? '')}
                                    defaultCountry="US"
                                    buttonClassName="border-[#F5F5F5]/30 bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/5"
                                    buttonStyle={{ color: '#F5F5F5' }}
                                    inputClassName={INPUT_CLS}
                                    popoverClassName="z-[10001]"
                                    aria-describedby={errors.phone ? `${formId}-phone-error` : undefined}
                                    required
                                />
                                {errors.phone && (
                                    <p id={`${formId}-phone-error`} role="alert" className="lux-body lux-body-sm mt-1 text-[#F87171]">
                                        {errors.phone}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label htmlFor={`${formId}-email`} className="lux-caps lux-caps-sm mb-2 block text-[#F5F5F5]/70">
                                    Email
                                </label>
                                <Input
                                    id={`${formId}-email`}
                                    type="email"
                                    value={data.email}
                                    onChange={(e) => setData('email', e.target.value)}
                                    className={INPUT_CLS}
                                    aria-describedby={errors.email ? `${formId}-email-error` : undefined}
                                    required
                                />
                                {errors.email && (
                                    <p id={`${formId}-email-error`} role="alert" className="lux-body lux-body-sm mt-1 text-[#F87171]">
                                        {errors.email}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Travel agent status + Current occupation — paired row */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <label htmlFor={`${formId}-is_travel_agent`} className="lux-caps lux-caps-sm mb-2 block text-[#F5F5F5]/70">
                                    Are you currently a travel agent?
                                </label>
                                <Select
                                    value={data.is_travel_agent}
                                    onValueChange={(val) => {
                                        if (val === 'no') {
                                            setData({ ...data, is_travel_agent: val, host_agency_name: '', annual_revenue: '' });
                                        } else {
                                            setData('is_travel_agent', val);
                                        }
                                    }}
                                    required
                                >
                                    <SelectTrigger id={`${formId}-is_travel_agent`} className={`${INPUT_CLS} data-[placeholder]:text-[#F5F5F5]/55`}>
                                        <SelectValue placeholder="Select one" />
                                    </SelectTrigger>
                                    <SelectContent className="z-[10001]">
                                        <SelectItem value="no">No</SelectItem>
                                        <SelectItem value="yes">Yes — I'm currently a travel agent</SelectItem>
                                    </SelectContent>
                                </Select>
                                {errors.is_travel_agent && (
                                    <p role="alert" className="lux-body lux-body-sm mt-1 text-[#F87171]">
                                        {errors.is_travel_agent}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label htmlFor={`${formId}-current_occupation`} className="lux-caps lux-caps-sm mb-2 block text-[#F5F5F5]/70">
                                    Current Occupation
                                </label>
                                <Input
                                    id={`${formId}-current_occupation`}
                                    value={data.current_occupation}
                                    onChange={(e) => setData('current_occupation', e.target.value)}
                                    className={INPUT_CLS}
                                    aria-describedby={errors.current_occupation ? `${formId}-occupation-error` : undefined}
                                    required
                                />
                                {errors.current_occupation && (
                                    <p id={`${formId}-occupation-error`} role="alert" className="lux-body lux-body-sm mt-1 text-[#F87171]">
                                        {errors.current_occupation}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Conditional: host agency name + annual revenue — paired row, only when "yes" */}
                        {data.is_travel_agent === 'yes' && (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <label htmlFor={`${formId}-host_agency_name`} className="lux-caps lux-caps-sm mb-2 block text-[#F5F5F5]/70">
                                        Host Agency Name
                                    </label>
                                    <Input
                                        id={`${formId}-host_agency_name`}
                                        value={data.host_agency_name}
                                        onChange={(e) => setData('host_agency_name', e.target.value)}
                                        placeholder="Your current host agency"
                                        className={INPUT_CLS}
                                        aria-describedby={errors.host_agency_name ? `${formId}-host-error` : undefined}
                                        required
                                    />
                                    {errors.host_agency_name && (
                                        <p id={`${formId}-host-error`} role="alert" className="lux-body lux-body-sm mt-1 text-[#F87171]">
                                            {errors.host_agency_name}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor={`${formId}-annual_revenue`} className="lux-caps lux-caps-sm mb-2 block text-[#F5F5F5]/70">
                                        Annual Revenue
                                    </label>
                                    <Select value={data.annual_revenue} onValueChange={(val) => setData('annual_revenue', val)} required>
                                        <SelectTrigger id={`${formId}-annual_revenue`} className={`${INPUT_CLS} data-[placeholder]:text-[#F5F5F5]/55`}>
                                            <SelectValue placeholder="Select a range" />
                                        </SelectTrigger>
                                        <SelectContent className="z-[10001]">
                                            <SelectItem value="under_500k">Under $500K</SelectItem>
                                            <SelectItem value="500k_to_1m">$500K – $1M</SelectItem>
                                            <SelectItem value="1m_to_5m">$1M – $5M</SelectItem>
                                            <SelectItem value="5m_to_10m">$5M – $10M</SelectItem>
                                            <SelectItem value="over_10m">Over $10M</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {errors.annual_revenue && (
                                        <p role="alert" className="lux-body lux-body-sm mt-1 text-[#F87171]">
                                            {errors.annual_revenue}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Essay */}
                        <div>
                            <label htmlFor={`${formId}-why_thrive`} className="lux-caps lux-caps-sm mb-2 block text-[#F5F5F5]/70">
                                Why would you thrive at our agency?
                            </label>
                            <Textarea
                                id={`${formId}-why_thrive`}
                                rows={5}
                                value={data.why_thrive}
                                onChange={(e) => setData('why_thrive', e.target.value)}
                                maxLength={WHY_THRIVE_MAX}
                                placeholder="Relevant skills, how you'll get your first clients, why you'd be a fit..."
                                className={`${INPUT_CLS} resize-none dark:bg-transparent`}
                                aria-describedby={errors.why_thrive ? `${formId}-why-error ${formId}-why-hint` : `${formId}-why-hint`}
                                required
                            />
                            <p id={`${formId}-why-hint`} className="lux-body lux-body-sm mt-1 flex flex-wrap gap-x-2 text-[#F5F5F5]/60">
                                <span>{data.why_thrive.trim().split(/\s+/).filter(Boolean).length} / 50 word minimum</span>
                                {/* The character count is the one that silently cost us applicants:
                                    anything past this never reached the CRM. Surface it only once
                                    they are close, so it reads as a heads-up and not a word limit. */}
                                {data.why_thrive.length > WHY_THRIVE_MAX - 400 && (
                                    <span className={data.why_thrive.length >= WHY_THRIVE_MAX ? 'text-[#F87171]' : 'text-[#F5F5F5]/80'}>
                                        {data.why_thrive.length >= WHY_THRIVE_MAX
                                            ? `${WHY_THRIVE_MAX.toLocaleString()} character maximum reached`
                                            : `${(WHY_THRIVE_MAX - data.why_thrive.length).toLocaleString()} characters left`}
                                    </span>
                                )}
                            </p>
                            {errors.why_thrive && (
                                <p id={`${formId}-why-error`} role="alert" className="lux-body lux-body-sm mt-1 text-[#F87171]">
                                    {errors.why_thrive}
                                </p>
                            )}
                        </div>

                        {/* SMS opt-in — checkbox + badge stay locked together as one inline unit;
                            only the long description can wrap to a new line on narrow viewports. */}
                        <div className="mt-8">
                            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center">
                                <label htmlFor={`${formId}-sms_opt_in`} className="flex shrink-0 items-center gap-x-2 cursor-pointer">
                                    <Checkbox
                                        id={`${formId}-sms_opt_in`}
                                        checked={data.sms_opt_in}
                                        onCheckedChange={(v) => setData('sms_opt_in', v === true)}
                                        className="border-[#F5F5F5]/30 data-[state=checked]:border-champagne data-[state=checked]:bg-champagne data-[state=checked]:text-[#0b2222]"
                                        required
                                    />
                                    <TaglineBadge
                                        text="SMS OPT-IN"
                                        className="h-3 w-auto text-[#F5F5F5]/60"
                                    />
                                </label>
                                <span className="lux-body lux-body-sm max-w-[16rem] leading-relaxed text-[#F5F5F5]/55 sm:max-w-none">
                                    Send appointment reminders by SMS. Msg &amp; data rates may apply.
                                </span>
                            </div>
                            {errors.sms_opt_in && (
                                <p role="alert" className="lux-body lux-body-sm mt-2 pl-7 text-[#F87171]">
                                    {errors.sms_opt_in}
                                </p>
                            )}
                        </div>

                        {/* Newsletter opt-in — temporarily hidden, restore when newsletter program launches. Mirrors the SMS row layout for visual parity. */}
                        {/* <div className="mt-4">
                            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center">
                                <label htmlFor={`${formId}-newsletter_opt_in`} className="flex shrink-0 cursor-pointer items-center gap-x-2">
                                    <Checkbox
                                        id={`${formId}-newsletter_opt_in`}
                                        checked={data.newsletter_opt_in}
                                        onCheckedChange={(v) => setData('newsletter_opt_in', v === true)}
                                        className="border-[#F5F5F5]/30 data-[state=checked]:border-champagne data-[state=checked]:bg-champagne data-[state=checked]:text-[#0b2222]"
                                    />
                                    <TaglineBadge text="EDITORIAL DISPATCH" className="h-3 w-auto text-[#F5F5F5]/60" />
                                </label>
                                <span className="lux-body lux-body-sm max-w-[16rem] leading-relaxed text-[#F5F5F5]/55 sm:max-w-none">
                                    Destination ideas, advisor picks, and partner offers, on occasion.
                                </span>
                            </div>
                        </div> */}

                        {/* Submit button — plain <button> for true parity with InquiryForm
                            (shadcn <Button> ships a default shadow-sm that lifts the surface
                            and reads as brightness on dark bg). */}
                        <div className="mt-8 mb-10 flex justify-center">
                            <button
                                type="submit"
                                disabled={processing}
                                className="lux-caps lux-caps-sm h-auto w-full cursor-pointer rounded-sm border border-[#F5F5F5]/30 bg-[#081c1c] px-4 py-2.5 text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#0a2020] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5F5F5]/60 disabled:opacity-50 sm:w-auto sm:py-1"
                            >
                                {processing ? 'Submitting…' : 'Submit Application'}
                            </button>
                        </div>

                        {/* Trust marks */}
                        <div className="flex items-center justify-center gap-6">
                            <img
                                src="/EndorsedByLogo-FTG-REV.png"
                                alt="Forbes Travel Guide Endorsed"
                                className="h-9 w-auto object-contain opacity-50"
                            />
                            <img src="/iata.svg" alt="IATA" className="h-6 w-auto object-contain opacity-50 brightness-0 invert" />
                            <img
                                src="/TLN-logo.png"
                                alt="Travel Leaders Network"
                                className="h-6 w-auto object-contain opacity-50 brightness-0 invert"
                            />
                            <img src="/arc.png" alt="ARC" className="h-6 w-auto object-contain opacity-50 brightness-0 invert" />
                        </div>
                    </motion.form>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
